"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

const routeLabels: Record<string, string> = {
  "services": "Services",
  "get-started": "Get Started",
  "connect": "Connect",
  "contact": "Contact Us",
  "about-us": "About Us",
  "mission": "Mission, Values & Goals",
  "team": "Our Team",
  "flamingo": "Why A Flamingo?",
  "faq": "FAQ",
  "contact-us": "Contact Us",
  "feedback": "Feedback",
  "flocktank": "FlockTank",
  "flockworker": "FlockWorker",
  "flocklist": "FlockList",
  "investors": "Investors",
  "entrepreneurs": "Entrepreneurs",
  "business-owners": "Business Owners",
  "public": "The Public",
  "core-policies": "Core Policies",
  "what-are-worker-coops": "Co-Op Basics",
  "supporters": "Supporters",
  "experts": "Experts",
  "legal": "Legal",
  "privacy": "Privacy Policy",
  "terms": "Terms of Service",
};

const virtualParents: Record<string, { label: string }> = {};

const nonLinkableRoutes = new Set(["services", "get-started", "connect", "legal"]);

function formatSegment(segment: string): string {
  return routeLabels[segment] || segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

interface BreadcrumbsProps {
  className?: string;
}

export default function Breadcrumbs({ className = "" }: BreadcrumbsProps) {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);

  const firstSegment = segments[0];
  const virtualParent = firstSegment ? virtualParents[firstSegment] : null;

  if (segments.length === 0 || (segments.length === 1 && !virtualParent)) {
    return null;
  }

  const breadcrumbs: { path: string; label: string; isLast: boolean; segment: string; isVirtual?: boolean }[] = [];

  if (virtualParent) {
    breadcrumbs.push({
      path: "",
      label: virtualParent.label,
      isLast: false,
      segment: "virtual-parent",
      isVirtual: true,
    });
  }

  segments.forEach((segment, index) => {
    const path = "/" + segments.slice(0, index + 1).join("/");
    const isLast = index === segments.length - 1;
    const label = formatSegment(segment);

    breadcrumbs.push({ path, label, isLast, segment });
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className={`py-3 px-4 md:px-8 bg-(--border-color) ${className}`}
    >
      <ol className="flex flex-wrap items-center gap-2 text-sm font-['Poppins'] max-w-7xl mx-auto">
        <li>
          <Link
            href="/"
            className="text-(--text-secondary) hover:text-(--brand) transition-colors"
          >
            Home
          </Link>
        </li>
        {breadcrumbs.map(({ path, label, isLast, segment, isVirtual }, index) => (
          <li key={isVirtual ? `virtual-${index}` : path} className="flex items-center gap-2">
            <span className="text-(--text-muted)">/</span>
            {isLast ? (
              <span className="text-(--brand) font-medium">{label}</span>
            ) : isVirtual || nonLinkableRoutes.has(segment) ? (
              <span className="text-(--text-muted)">{label}</span>
            ) : (
              <Link
                href={path}
                className="text-(--text-secondary) hover:text-(--brand) transition-colors"
              >
                {label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
